import { useState } from 'react';
import { Button, Modal, TextAreaField, TextField } from '../primitives';
import { useActiveOrg } from '../../lib/activeOrg';
import { useCreateProject } from '../../lib/projects/useCreateProject';

export interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function CreateProjectModal({ open, onClose, onCreated }: CreateProjectModalProps) {
  const { activeOrgId } = useActiveOrg();
  const { creating, error, create } = useCreateProject();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const canSubmit = name.trim().length > 0 && !creating && !!activeOrgId;

  const onSubmit = async () => {
    if (!activeOrgId || !canSubmit) return;
    const id = await create(activeOrgId, name, description);
    if (id) { onCreated(id); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} data-testid="create-project-cancel">Cancel</Button>
          <Button onClick={onSubmit} disabled={!canSubmit} data-testid="create-project-submit">
            {creating ? 'Creating…' : 'Create project'}
          </Button>
        </>
      }
    >
      <div className="pn-create-project">
        <TextField
          id="create-project-name-input"
          label="Project name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          data-testid="create-project-name"
          autoFocus
        />
        <TextAreaField
          id="create-project-desc-input"
          label="Description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          data-testid="create-project-desc"
        />
        {error ? <p className="pn-create-project-error" role="alert">{error}</p> : null}
      </div>
    </Modal>
  );
}
